import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

interface ShimmerLoadingProps {
    type?: 'dashboard' | 'programs' | 'table' | 'form';
    fullScreen?: boolean;
}

export const ShimmerLoading: React.FC<ShimmerLoadingProps> = ({
    type = 'dashboard',
    fullScreen = false
}) => {
    const renderDashboardShimmer = () => (
        <Box sx={{ p: 3, backgroundColor: '#0a0a0a', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Skeleton variant="text" width={300} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            </Box>

            {/* Stats Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                {[1, 2, 3, 4].map((item) => (
                    <Box
                        key={item}
                        sx={{
                            flex: '1 1 250px',
                            minWidth: '250px',
                            maxWidth: '400px',
                            height: '160px',
                            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)',
                            borderRadius: 3,
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box>
                            <Skeleton variant="text" width={80} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
                            <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                        </Box>
                        <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                    </Box>
                ))}
            </Box>

            {/* Table/Content Area */}
            <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 2 }}>
                <Skeleton variant="rectangular" width="100%" height={400} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            </Box>
        </Box>
    );

    const renderProgramsShimmer = () => (
        <Box sx={{ p: 3, backgroundColor: '#0a0a0a', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Skeleton variant="text" width={200} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                <Skeleton variant="rectangular" width={120} height={40} sx={{ bgcolor: 'rgba(33, 150, 243, 0.3)' }} />
            </Box>

            {/* Stats Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                {[1, 2, 3, 4].map((item) => (
                    <Box
                        key={item}
                        sx={{
                            flex: '1 1 250px',
                            minWidth: '250px',
                            maxWidth: '400px',
                            height: '160px',
                            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)',
                            borderRadius: 3,
                            p: 3,
                        }}
                    >
                        <Skeleton variant="text" width={80} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
                        <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                    </Box>
                ))}
            </Box>

            {/* Table */}
            <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 2 }}>
                <Skeleton variant="rectangular" width="100%" height={50} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
                {[1, 2, 3, 4, 5].map((item) => (
                    <Box key={item} sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Skeleton variant="rectangular" width="20%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Skeleton variant="rectangular" width="25%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Skeleton variant="rectangular" width="20%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Skeleton variant="rectangular" width="15%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Skeleton variant="rectangular" width="20%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    </Box>
                ))}
            </Box>
        </Box>
    );

    const renderFormShimmer = () => (
        <Box sx={{ p: 3, backgroundColor: '#0a0a0a', minHeight: '100vh' }}>
            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                <Skeleton variant="text" width={200} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

                <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 3 }}>
                    {[1, 2, 3, 4, 5].map((item) => (
                        <Box key={item} sx={{ mb: 2 }}>
                            <Skeleton variant="text" width={100} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', mb: 1 }} />
                            <Skeleton variant="rectangular" width="100%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        </Box>
                    ))}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                        <Skeleton variant="rectangular" width={80} height={36} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Skeleton variant="rectangular" width={80} height={36} sx={{ bgcolor: 'rgba(33, 150, 243, 0.3)' }} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    const getShimmerContent = () => {
        switch (type) {
            case 'programs':
                return renderProgramsShimmer();
            case 'form':
                return renderFormShimmer();
            case 'dashboard':
            default:
                return renderDashboardShimmer();
        }
    };

    if (fullScreen) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    overflow: 'auto',
                }}
            >
                {getShimmerContent()}
            </Box>
        );
    }

    return getShimmerContent();
};




















